import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Terminal, ArrowRight, ShieldAlert } from "lucide-react";
import { adminClient, setAdminToken, getAdminToken, formatApiError } from "@/lib/adminApi";
import { ADMIN } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_prompt-mastery-64/artifacts/3omzwe9h_1773800644378.png";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (getAdminToken()) return <Navigate to="/admin/leads" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await adminClient.post("/admin/login", {
        email: email.trim(),
        password,
      });
      setAdminToken(data.access_token);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10 bg-grid opacity-50 animate-grid-pan" />
      <div className="absolute inset-0 -z-10 bg-radial-glow" />

      <div className="min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Brand */}
          <div className="flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="JK Academy" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-extrabold tracking-tight text-white text-base">
                JK ACADEMY
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
                Admin · Restricted
              </div>
            </div>
          </div>

          <div
            data-testid={ADMIN.loginCard}
            className="relative bg-slate-900/60 backdrop-blur-md border border-white/10 p-8 sm:p-10 shadow-[0_0_60px_rgba(6,182,212,0.15)]"
          >
            {/* Corner ticks */}
            <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
            <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
            <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
            <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-6">
              <Terminal className="h-3.5 w-3.5" />
              <span>/ admin.session.login</span>
            </div>

            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight">
              Secure access
            </h1>
            <p className="mt-2 text-slate-400 font-mono text-sm">
              Sign in to view captured leads.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
              <div>
                <label
                  htmlFor="email"
                  className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2"
                >
                  Admin email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  data-testid={ADMIN.loginEmail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@jkacademy.com"
                  className="w-full bg-slate-950 border border-white/10 text-white placeholder:text-slate-600 h-12 px-4 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  data-testid={ADMIN.loginPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-950 border border-white/10 text-white placeholder:text-slate-600 h-12 px-4 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                />
              </div>

              {error && (
                <div
                  data-testid={ADMIN.loginError}
                  className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-3 py-2.5"
                >
                  <ShieldAlert className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="font-mono text-xs text-red-300 leading-snug">{error}</p>
                </div>
              )}

              <button
                type="submit"
                data-testid={ADMIN.loginSubmit}
                disabled={submitting}
                className="w-full group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_42px_rgba(6,182,212,0.7)] transition-all"
              >
                <Lock className="h-4 w-4" />
                {submitting ? "Authenticating…" : "Sign in"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>

          <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Authorized personnel only
          </p>
        </div>
      </div>
    </div>
  );
}
